import {
  COVER_DOC_LIMITS,
  type CoverBackground,
  type CoverDoc,
  type CoverEffect,
  type CoverTextBox,
} from 'fym-shared/src/music24/coverDoc';

/** What is selected on the cover: a text box, an effect, or nothing (the cover itself). */
export type CoverSelection = { kind: 'text'; id: string } | { kind: 'effect'; id: string } | null;

/** The most steps that Undo keeps. */
export const COVER_HISTORY_LIMIT = 100;
/** Two changes with the same key inside this time are one Undo step (a drag, a slider). */
export const COVER_COALESCE_MS = 800;

export interface CoverEditorState {
  doc: CoverDoc;
  past: CoverDoc[];
  future: CoverDoc[];
  selection: CoverSelection;
  /** The key and the time of the last change, for the one-step rule. */
  lastKey: string | null;
  lastAt: number;
  /** Goes up with each change of the document: the autosave and the canvas watch it. */
  revision: number;
}

interface Timed {
  /** The key of a run of changes that is one Undo step (`text:<id>:move`). */
  coalesce?: string;
  /** The time of the action in ms; the caller gives it, so the reducer stays pure. */
  at?: number;
}

export type CoverEditorAction =
  | { type: 'replaceDoc'; doc: CoverDoc; keepHistory?: boolean }
  | ({ type: 'setBackground'; background: CoverBackground; palette?: string[] } & Timed)
  | { type: 'setPalette'; palette: string[] }
  | { type: 'addText'; box: CoverTextBox }
  | ({ type: 'updateText'; id: string; patch: Partial<Omit<CoverTextBox, 'id'>> } & Timed)
  | { type: 'removeText'; id: string }
  | { type: 'duplicateText'; id: string; newId: string }
  | { type: 'moveText'; id: string; toIndex: number }
  | { type: 'applyLayout'; texts: CoverTextBox[] }
  | { type: 'addEffect'; effect: CoverEffect }
  | ({ type: 'updateEffect'; id: string; patch: Partial<Omit<CoverEffect, 'id' | 'type'>> } & Timed)
  | { type: 'removeEffect'; id: string }
  | { type: 'moveEffect'; id: string; toIndex: number }
  | { type: 'select'; selection: CoverSelection }
  | { type: 'commit' }
  | { type: 'undo' }
  | { type: 'redo' };

export function initialCoverEditorState(doc: CoverDoc): CoverEditorState {
  return { doc, past: [], future: [], selection: null, lastKey: null, lastAt: 0, revision: 0 };
}

/** Moves the item with `id` to `toIndex` (clamped). The same list when the id is not in it. Pure. */
export function moveById<T extends { id: string }>(list: readonly T[], id: string, toIndex: number): T[] {
  const from = list.findIndex((item) => item.id === id);
  const item = list[from];
  if (item === undefined) {
    return [...list];
  }
  const rest = list.filter((other) => other.id !== id);
  const index = Math.min(rest.length, Math.max(0, toIndex));
  return [...rest.slice(0, index), item, ...rest.slice(index)];
}

/** The state after a change of the document: one more Undo step, or the same step for a run. */
function withDoc(state: CoverEditorState, doc: CoverDoc, timed: Timed = {}): CoverEditorState {
  const at = timed.at ?? 0;
  const sameRun =
    timed.coalesce !== undefined && timed.coalesce === state.lastKey && at - state.lastAt <= COVER_COALESCE_MS;
  return {
    ...state,
    doc,
    past: sameRun ? state.past : [...state.past, state.doc].slice(-COVER_HISTORY_LIMIT),
    future: [],
    lastKey: timed.coalesce ?? null,
    lastAt: at,
    revision: state.revision + 1,
  };
}

/** The selection, or null when its item is not in the document any more. */
function keptSelection(selection: CoverSelection, doc: CoverDoc): CoverSelection {
  if (selection === null) {
    return null;
  }
  const list: ReadonlyArray<{ id: string }> = selection.kind === 'text' ? doc.texts : doc.effects;
  return list.some((item) => item.id === selection.id) ? selection : null;
}

/**
 * The document of the editor with Undo and Redo (design cover-editor b1). Pure. A change with a
 * `coalesce` key joins the step before it when that step has the same key and is at most
 * `COVER_COALESCE_MS` old, so a drag or a slider move is one Undo step. `commit` ends a run. The
 * selection is not a part of the history. A list at its limit refuses a new item.
 */
export function coverEditorReducer(state: CoverEditorState, action: CoverEditorAction): CoverEditorState {
  const { doc } = state;
  switch (action.type) {
    case 'replaceDoc':
      return action.keepHistory === true
        ? { ...withDoc(state, action.doc), selection: keptSelection(state.selection, action.doc) }
        : { ...initialCoverEditorState(action.doc), revision: state.revision + 1 };
    case 'setBackground':
      return withDoc(state, { ...doc, background: action.background, palette: action.palette ?? doc.palette }, action);
    case 'setPalette':
      return withDoc(state, { ...doc, palette: action.palette.slice(0, COVER_DOC_LIMITS.palette) });
    case 'addText':
      if (doc.texts.length >= COVER_DOC_LIMITS.texts || doc.texts.some((box) => box.id === action.box.id)) {
        return state;
      }
      return {
        ...withDoc(state, { ...doc, texts: [...doc.texts, action.box] }),
        selection: { kind: 'text', id: action.box.id },
      };
    case 'updateText':
      if (!doc.texts.some((box) => box.id === action.id)) {
        return state;
      }
      return withDoc(
        state,
        { ...doc, texts: doc.texts.map((box) => (box.id === action.id ? { ...box, ...action.patch } : box)) },
        action
      );
    case 'removeText': {
      const next = { ...doc, texts: doc.texts.filter((box) => box.id !== action.id) };
      return next.texts.length === doc.texts.length
        ? state
        : { ...withDoc(state, next), selection: keptSelection(state.selection, next) };
    }
    case 'duplicateText': {
      const source = doc.texts.find((box) => box.id === action.id);
      if (
        source === undefined ||
        doc.texts.length >= COVER_DOC_LIMITS.texts ||
        doc.texts.some((box) => box.id === action.newId)
      ) {
        return state;
      }
      // The copy is a free box a little down and right, so both show.
      const copy: CoverTextBox = { ...source, id: action.newId, role: 'free', x: source.x + 0.03, y: source.y + 0.03 };
      return {
        ...withDoc(state, { ...doc, texts: [...doc.texts, copy] }),
        selection: { kind: 'text', id: copy.id },
      };
    }
    case 'moveText':
      return withDoc(state, { ...doc, texts: moveById(doc.texts, action.id, action.toIndex) });
    case 'applyLayout': {
      // A layout places the title and the artist. The free boxes of the user stay.
      const free = doc.texts.filter((box) => box.role === 'free');
      const next = { ...doc, texts: [...action.texts, ...free].slice(0, COVER_DOC_LIMITS.texts) };
      return { ...withDoc(state, next), selection: keptSelection(state.selection, next) };
    }
    case 'addEffect':
      if (doc.effects.length >= COVER_DOC_LIMITS.effects || doc.effects.some((fx) => fx.id === action.effect.id)) {
        return state;
      }
      return {
        ...withDoc(state, { ...doc, effects: [...doc.effects, action.effect] }),
        selection: { kind: 'effect', id: action.effect.id },
      };
    case 'updateEffect':
      if (!doc.effects.some((fx) => fx.id === action.id)) {
        return state;
      }
      return withDoc(
        state,
        { ...doc, effects: doc.effects.map((fx) => (fx.id === action.id ? { ...fx, ...action.patch } : fx)) },
        action
      );
    case 'removeEffect': {
      const next = { ...doc, effects: doc.effects.filter((fx) => fx.id !== action.id) };
      return next.effects.length === doc.effects.length
        ? state
        : { ...withDoc(state, next), selection: keptSelection(state.selection, next) };
    }
    case 'moveEffect':
      return withDoc(state, { ...doc, effects: moveById(doc.effects, action.id, action.toIndex) });
    case 'select':
      return { ...state, selection: keptSelection(action.selection, doc), lastKey: null };
    case 'commit':
      return state.lastKey === null ? state : { ...state, lastKey: null };
    case 'undo': {
      const previous = state.past[state.past.length - 1];
      if (previous === undefined) {
        return state;
      }
      return {
        ...state,
        doc: previous,
        past: state.past.slice(0, -1),
        future: [doc, ...state.future],
        selection: keptSelection(state.selection, previous),
        lastKey: null,
        revision: state.revision + 1,
      };
    }
    case 'redo': {
      const [next, ...rest] = state.future;
      if (next === undefined) {
        return state;
      }
      return {
        ...state,
        doc: next,
        past: [...state.past, doc],
        future: rest,
        selection: keptSelection(state.selection, next),
        lastKey: null,
        revision: state.revision + 1,
      };
    }
    default: {
      const unknown: never = action;
      return unknown;
    }
  }
}
